// src/components/ThemeConfigurator/ThemeConfigurator.jsx
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTheme, PRESET_THEMES } from '../../contexts/ThemeContext';
import { 
  Palette, X, Moon, Sun, Sliders, Check, 
  RotateCcw, ZoomIn, ZoomOut
} from 'lucide-react';

// Main container - completely hidden when closed
const ConfiguratorContainer = styled.div`
  position: fixed;
  bottom: ${({ $isOpen }) => $isOpen ? '2rem' : '-2000px'};
  right: 2rem;
  z-index: 1100; /* Increased z-index to appear above most elements */
  transition: bottom 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 300px;
  background: ${({ theme }) => theme.colors.card};
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  overflow: hidden;
  opacity: ${({ $isOpen }) => $isOpen ? '1' : '0'};
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  
  @media (max-width: 768px) {
    width: calc(100% - 2rem);
    right: 1rem;
  }
`;

// Toggle button to show/hide configurator
const ConfiguratorToggle = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: var(--accent);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  z-index: 1101; /* Slightly higher than container */
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: rotate(30deg);
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

// Header of the configurator
const ConfiguratorHeader = styled.div`
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondary};
  
  h3 {
    margin: 0;
    font-size: 1rem;
    color: ${({ theme }) => theme.colors.text};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  button {
    background: transparent;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.textSecondary};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.25rem;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
      color: ${({ theme }) => theme.colors.text};
      background: ${({ theme }) => theme.colors.secondary};
    }
  }
`;

// Theme section
const ThemeSection = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondary};
`;

const SectionTitle = styled.h4`
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ThemeToggle = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const ToggleButton = styled.button`
  background: ${({ theme, $active }) => 
    $active ? theme.colors.accent : theme.colors.secondary};
  color: ${({ $active }) => $active ? 'white' : 'inherit'};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme, $active }) => 
      $active ? theme.colors.accent : `${theme.colors.secondary}dd`};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Theme presets
const PresetContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  margin: 1rem 0;
`;

const PresetButton = styled.button`
  background: ${({ theme }) => theme.colors.secondary};
  border: 2px solid ${({ $active, theme }) => 
    $active ? theme.colors.accent : 'transparent'};
  border-radius: 8px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
  
  .preset-name {
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.text};
  }
  
  .color-preview {
    display: flex;
    gap: 0.25rem;
  }
  
  .color-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
  }
`;

// Color pickers
const ColorPickerContainer = styled.div`
  margin: 1rem 0;
`;

const ColorPickerRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  
  label {
    flex: 1;
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
  
  input[type="color"] {
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 4px;
    background: none;
    cursor: pointer;
  }
`;

// Custom font size controls
const FontSizeControls = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondary};
`;

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0;
  
  input[type="range"] {
    flex: 1;
    -webkit-appearance: none;
    appearance: none;
    height: 4px;
    background: ${({ theme }) => theme.colors.secondary};
    border-radius: 2px;
    outline: none;
    
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: ${({ theme }) => theme.colors.accent};
      cursor: pointer;
    }
    
    &::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: ${({ theme }) => theme.colors.accent};
      cursor: pointer;
      border: none;
    }
  }
  
  button {
    background: ${({ theme }) => theme.colors.secondary};
    border: none;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.text};
    
    &:hover {
      background: ${({ theme }) => `${theme.colors.secondary}dd`};
    }
  }
  
  .size-value {
    min-width: 32px;
    text-align: center;
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.text};
  }
`;

// Action buttons
const ActionButtons = styled.div`
  padding: 1rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: ${({ theme, $primary }) => 
    $primary ? theme.colors.accent : theme.colors.secondary};
  color: ${({ $primary }) => $primary ? 'white' : 'inherit'};
  border: none;
  padding: 0.75rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    background: ${({ theme, $primary }) => 
      $primary ? `${theme.colors.accent}dd` : `${theme.colors.secondary}dd`};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Main component implementation
const ThemeConfigurator = () => {
  const { 
    isDark, 
    toggleTheme, 
    activePreset, 
    customColors, 
    applyPreset, 
    applyCustomColors, 
    updateColor,
    resetTheme 
  } = useTheme();
  
  // Panel starts closed
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(() => {
    // Initialize font size from current document or default to 16px
    const currentSize = parseInt(getComputedStyle(document.documentElement).fontSize);
    return isNaN(currentSize) ? 16 : currentSize;
  });
  
  // Use local state to track changes before applying them
  const [tempColors, setTempColors] = useState({...customColors});
  
  // Toggle configurator
  const toggleConfigurator = () => {
    if (!isOpen) {
      // Initialize tempColors from customColors when opening
      setTempColors({...customColors});
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };
  
  // Handle color change for individual colors - using useCallback for optimization
  const handleColorChange = useCallback((colorKey, value) => {
    // Update temporary colors
    setTempColors(prev => ({
      ...prev,
      [colorKey]: value
    }));
    
    // Apply changes immediately to see preview
    updateColor(colorKey, value);
  }, [updateColor]);
  
  // Handle font size change - using useCallback for optimization
  const changeFontSize = useCallback((delta) => {
    setFontSize(prev => {
      const newSize = Math.max(12, Math.min(20, prev + delta));
      document.documentElement.style.fontSize = `${newSize}px`;
      return newSize;
    });
  }, []);
  
  // Apply all changes and close panel
  const applyChanges = useCallback(() => {
    // Apply custom colors (this will update the theme context)
    applyCustomColors(tempColors);
    
    // Update CSS variables
    Object.entries(tempColors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--${key}`, value);
    });
    
    // Don't close panel after applying, let user see the changes
  }, [applyCustomColors, tempColors]);
  
  // Reset to defaults 
  const handleReset = useCallback(() => {
    resetTheme();
    setFontSize(16);
    document.documentElement.style.fontSize = '16px';
    
    // Reset CSS variables
    const defaultColors = isDark ? PRESET_THEMES.default.dark : PRESET_THEMES.default.light;
    Object.entries(defaultColors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--${key}`, value);
    });
    
    // Update tempColors to match the reset theme
    setTempColors(defaultColors);
  }, [isDark, resetTheme]);

  // Sync CSS variables with theme changes
  useEffect(() => {
    // When theme changes, update CSS variables
    Object.entries(customColors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--${key}`, value);
    });
  }, [customColors]);

  // Update tempColors when customColors changes (e.g., when the theme gets toggled)
  useEffect(() => {
    setTempColors({...customColors});
  }, [customColors]);

  // Handle keyboard events for accessibility (Escape key to close panel)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <>
      {/* Toggle button */}
      <ConfiguratorToggle onClick={toggleConfigurator} aria-label="Theme settings">
        <Palette />
      </ConfiguratorToggle>
      
      {/* Configurator panel */}
      <ConfiguratorContainer $isOpen={isOpen}>
        <ConfiguratorHeader>
          <h3>
            <Sliders size={16} />
            Theme Settings
          </h3>
          <button onClick={toggleConfigurator} aria-label="Close theme settings">
            <X size={18} />
          </button>
        </ConfiguratorHeader>
        
        {/* Theme mode toggle */}
        <ThemeSection>
          <SectionTitle>Theme Mode</SectionTitle>
          <ThemeToggle>
            <ToggleButton 
              onClick={toggleTheme} 
              $active={!isDark}
              aria-label="Light mode"
            >
              <Sun size={16} />
              Light
            </ToggleButton>
            <ToggleButton 
              onClick={toggleTheme} 
              $active={isDark}
              aria-label="Dark mode"
            >
              <Moon size={16} />
              Dark
            </ToggleButton>
          </ThemeToggle>
          
          {/* Theme presets */}
          <SectionTitle>Presets</SectionTitle>
          <PresetContainer>
            {Object.keys(PRESET_THEMES).map(preset => (
              <PresetButton
                key={preset}
                $active={activePreset === preset}
                onClick={() => {
                  applyPreset(preset);
                  
                  // Update CSS variables for immediate effect
                  const presetColors = isDark ? 
                    PRESET_THEMES[preset].dark : 
                    PRESET_THEMES[preset].light;
                    
                  Object.entries(presetColors).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                  });
                  
                  // Update tempColors to match the selected preset
                  setTempColors(presetColors);
                }}
                aria-label={`${preset} theme preset`}
              >
                <div className="color-preview">
                  <div 
                    className="color-dot" 
                    style={{ 
                      background: isDark 
                        ? PRESET_THEMES[preset].dark.accent 
                        : PRESET_THEMES[preset].light.accent 
                    }}
                  />
                  <div 
                    className="color-dot" 
                    style={{ 
                      background: isDark 
                        ? PRESET_THEMES[preset].dark.bg 
                        : PRESET_THEMES[preset].light.bg 
                    }}
                  />
                  <div 
                    className="color-dot" 
                    style={{ 
                      background: isDark 
                        ? PRESET_THEMES[preset].dark.card 
                        : PRESET_THEMES[preset].light.card 
                    }}
                  />
                </div>
                <span className="preset-name">
                  {preset.charAt(0).toUpperCase() + preset.slice(1)}
                  {activePreset === preset && (
                    <Check size={12} style={{ marginLeft: '4px' }} />
                  )}
                </span>
              </PresetButton>
            ))}
          </PresetContainer>
          
          {/* Custom colors */}
          <SectionTitle>Custom Colors</SectionTitle>
          <ColorPickerContainer>
            <ColorPickerRow>
              <label htmlFor="accent-color">Accent</label>
              <input 
                type="color" 
                id="accent-color"
                value={tempColors.accent}
                onChange={(e) => handleColorChange('accent', e.target.value)}
                aria-label="Choose accent color"
              />
            </ColorPickerRow>
            <ColorPickerRow>
              <label htmlFor="bg-color">Background</label>
              <input 
                type="color" 
                id="bg-color"
                value={tempColors.bg}
                onChange={(e) => handleColorChange('bg', e.target.value)}
                aria-label="Choose background color"
              />
            </ColorPickerRow>
            <ColorPickerRow>
              <label htmlFor="card-color">Card</label>
              <input 
                type="color" 
                id="card-color"
                value={tempColors.card}
                onChange={(e) => handleColorChange('card', e.target.value)}
                aria-label="Choose card color"
              />
            </ColorPickerRow>
            <ColorPickerRow>
              <label htmlFor="text-color">Text</label>
              <input 
                type="color" 
                id="text-color"
                value={tempColors.text}
                onChange={(e) => handleColorChange('text', e.target.value)}
                aria-label="Choose text color"
              />
            </ColorPickerRow>
            <ColorPickerRow>
              <label htmlFor="text-secondary-color">Secondary Text</label>
              <input 
                type="color" 
                id="text-secondary-color"
                value={tempColors.textSecondary}
                onChange={(e) => handleColorChange('textSecondary', e.target.value)}
                aria-label="Choose secondary text color"
              />
            </ColorPickerRow>
            <ColorPickerRow>
              <label htmlFor="secondary-color">Secondary</label>
              <input 
                type="color" 
                id="secondary-color"
                value={tempColors.secondary}
                onChange={(e) => handleColorChange('secondary', e.target.value)}
                aria-label="Choose secondary color"
              />
            </ColorPickerRow>
          </ColorPickerContainer>
        </ThemeSection>
        
        {/* Font size controls */}
        <FontSizeControls>
          <SectionTitle>Font Size</SectionTitle>
          <SliderContainer>
            <button onClick={() => changeFontSize(-1)} aria-label="Decrease font size">
              <ZoomOut size={16} />
            </button>
            <input 
              type="range" 
              min="12" 
              max="20" 
              value={fontSize}
              onChange={(e) => {
                const size = parseInt(e.target.value);
                setFontSize(size);
                document.documentElement.style.fontSize = `${size}px`;
              }}
              aria-label="Font size"
            />
            <button onClick={() => changeFontSize(1)} aria-label="Increase font size">
              <ZoomIn size={16} />
            </button>
            <span className="size-value">{fontSize}px</span>
          </SliderContainer>
        </FontSizeControls>
        
        {/* Action buttons */}
        <ActionButtons>
          <ActionButton onClick={handleReset} aria-label="Reset to defaults">
            <RotateCcw size={16} />
            Reset
          </ActionButton>
          <ActionButton 
            $primary 
            onClick={applyChanges} 
            aria-label="Apply theme changes"
          >
            <Check size={16} />
            Apply
          </ActionButton>
        </ActionButtons>
      </ConfiguratorContainer>
    </>
  );
};

export default ThemeConfigurator;